import { f7 } from 'framework7-react';
import { useEffect, useMemo, useRef, useState } from 'react';

interface PickerDateHookProps {
  initialDate: Date | undefined;
}

export const usePickerDate = ({ initialDate }: PickerDateHookProps) => {
  const pickerRef = useRef<any>(null);
  const currentDate = useMemo(() => new Date(), []);

  const [pickerSelect, setPickerSelect] = useState('');
  const pickerInputID = useMemo(() => 'date-picker-input', []);
  const pickerContainerID = useMemo(() => 'date-picker-container', []);

  const offset = 1;
  const start = 2020;
  const yearList = useMemo(() => {
    const end = currentDate.getFullYear();
    const length = end - start;
    return Array.from({ length }, (_, idx) => `${start + idx + offset}`);
  }, []);
  const monthList = useMemo(() => {
    const length = 12;
    return Array.from({ length }, (_, idx) => `${idx + offset}`);
  }, []);
  const dateList = useMemo(() => {
    const length = 31;
    return Array.from({ length }, (_, idx) => `${idx + offset}`);
  }, []);
  useEffect(() => {
    if (!pickerRef.current) {
      pickerRef.current = f7.picker.create({
        inputEl: `#${pickerInputID}`,
        containerEl: `#${pickerContainerID}`,
        toolbar: false,
        rotateEffect: true,
        value: [
          currentDate.getFullYear(),
          0,
          currentDate.getMonth() + offset,
          0,
          currentDate.getDate(),
          0,
        ],
        cols: [
          { values: yearList },
          { displayValues: ['年'], values: ['0'] },
          { values: monthList },
          { displayValues: ['月'], values: ['0'] },
          { values: dateList },
          { displayValues: ['日'], values: ['0'] },
        ],
        on: {
          change: function (picker, values) {
            const valuesList = values as number[];
            const dateItem = valuesList.filter(
              (_, idx) => idx % 2 === 0 || idx === 0,
            );
            const pad = (num: number) => {
              const zeroPad = 2;
              return num.toString().padStart(zeroPad, '0');
            };
            const yearIdx = 0;
            const monthIdx = 1;
            const dateIdx = 2;
            const update = new Date(
              `${dateItem[yearIdx]}-${pad(dateItem[monthIdx])}-${pad(
                dateItem[dateIdx],
              )}T00:00:00.000Z`,
            );
            if (
              !!(update instanceof Date && !isNaN(update.getTime())) &&
              !!(update.getMonth() + offset === Number(dateItem[monthIdx]))
            ) {
              dateItem.forEach((item, idx) => {
                const correction = 2;
                picker.cols[idx * correction].setValue(item);
              });
              setPickerSelect(
                `${dateItem[yearIdx]}年${dateItem[monthIdx]}月${dateItem[dateIdx]}日`,
              );
            } else {
              const lastDate = 0;
              const fixDate = new Date(
                dateItem[yearIdx],
                dateItem[monthIdx],
                lastDate,
              );
              const fixItem = [
                fixDate.getFullYear(),
                fixDate.getMonth() + offset,
                fixDate.getDate(),
              ];
              fixItem.forEach((item, idx) => {
                const correction = 2;
                picker.cols[idx * correction].setValue(item);
              });
              setPickerSelect(
                `${fixItem[yearIdx]}年${fixItem[monthIdx]}月${fixItem[dateIdx]}日`,
              );
            }
          },
        },
      });
    }
  }, []);

  return {
    pickerSelect,
    pickerInputID,
    pickerContainerID,
  };
};
